import { min, max } from 'd3-array';

import Indicator from '../Indicator';
import LineMarker from '../markers/LineMarker';

/** DMI indicator */
export default class DMIIndicator extends Indicator {


	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {Object} - Default parameters object
	 * @property {String} id - Indicator ID
	 * @property {String} name - Indicator name
	 * @property {String} markerType - Render the indicator in different ways
	 * @property {String} description - Indicator description
	 * @property {Object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'dmi',
			name: 'DMI',
			description:
				'The DMI is a trend indicator that uses high and low prices as well as the true range to determine whether a security is in an uptrend or a downtrend. The ADX line describes the strength of the trend.',
			inputs: [{ name: 'period', value: 14 }],
			markerType: 'default', // "default", "adx", "adxr"
			componentSeries: [
				{ id: 'adx', styleRef: 'adx' }
			],
			dataSeries: 'adx'
		};
	}

	/**
	 * @param {Object} data - Symbol data object
	 * @return {Number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataDMI = data.dmi || [];

		if (this.params.markerType === 'adx') {
			return min(dataDMI, i => +i.adx);
		} else if (this.params.markerType === 'adxr') {
			return this._min(
				min(dataDMI, i => +i.adxr),
				min(dataDMI, i => +i.adx)
			);
		}

		return this._min(
			min(dataDMI, i => +i.minusdi),
			min(dataDMI, i => +i.plusdi),
			min(dataDMI, i => +i.adx)
		);
	}

	/**
	 * @param {Object} data - Symbol data object
	 * @return {Number} - d3-range maximum
	 */
	getRangeMax(data) {
		const dataDMI = data.dmi || [];

		if (this.params.markerType === 'adx') {
			return max(dataDMI, i => +i.adx);
		} else if (this.params.markerType === 'adxr') {
			return this._max(
				max(dataDMI, i => +i.adxr),
				max(dataDMI, i => +i.adx)
			);
		}

		return this._max(
			max(dataDMI, i => +i.minusdi),
			max(dataDMI, i => +i.plusdi),
			max(dataDMI, i => +i.adx)
		);
	}

	/**
	 * @returns {Object}
	 * @property {LineMarker} lineADX
	 * @property {LineMarker} lineADXR
	 * @property {LineMarker} lineMinusDI
	 * @property {LineMarker} linePlusDI
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineADX: new LineMarker(),
				lineADXR: new LineMarker(),
				lineMinusDI: new LineMarker(),
				linePlusDI: new LineMarker()
			}
		);
	}

	/** @param {Object} data - Symbol data object */
	render(data, sessionType) {

		if (this.params.markerType === 'adx') {

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adx'
			});

		} else if (this.params.markerType === 'adxr') {

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adx'
			});

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adxr'
			});

		} else {

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineMinusDI,
				dataKey: 'minusdi',
				styleKey: 'negative'
			});

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.linePlusDI,
				dataKey: 'plusdi',
				styleKey: 'positive'
			});

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adx'
			});
		}
	}
}
